import React, { useState, useRef } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  IconButton, 
  Paper, 
  Divider, 
  Chip,
  Grid,
  Tooltip
  // Tabs,
  // Tab
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { 
  Mic as MicIcon, 
  MicOff as MicOffIcon, 
  Send as SendIcon, 
  Upload as UploadIcon, 
  Link as LinkIcon,
  Save as SaveIcon,
  Help as HelpIcon,
  AttachFile as AttachFileIcon,
  TextFields as TextFieldsIcon
} from '@mui/icons-material';
import GradientText from '../../../components/common/GradientText.tsx';
import VoiceWaveform from './VoiceWaveform.tsx';
import { useNavigate } from 'react-router-dom';

interface ProblemSubmissionAreaProps {
  onSubmit: (problem: string) => void;
  onInputMethodSelected?: () => void;
}

const SubmissionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 12,
    left: 0,
    width: '4px',
    height: '98%',
    background: 'linear-gradient(to bottom, #00CC99,  #8A2BE2)',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  fontWeight: 600,
  position: 'relative',
  display: 'inline-block',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -5,
    left: 0,
    width: '100%',
    height: '2px',
    background: 'linear-gradient(90deg, #00CC99, #8A2BE2)',
  }
}));

const InputContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.3)',
  marginBottom: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(0, 255, 255, 0.3))',
    transform: 'rotate(-45deg)',
    transition: 'all 0.5s ease',
    opacity: 0,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    backgroundSize: '300% 100%',
    animation: 'gradient 6s linear infinite',
  },
  '&:hover': {
    boxShadow: '0 0 20px rgba(138, 43, 226, 0.3)',
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  },
  '@keyframes gradient': {
    '0%': {
      backgroundPosition: '0% 50%'
    },
    '50%': {
      backgroundPosition: '100% 50%'
    },
    '100%': {
      backgroundPosition: '0% 50%'
    }
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: theme.spacing(1),
    '& fieldset': {
      borderColor: 'rgba(138, 43, 226, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(138, 43, 226, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#8A2BE2',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiOutlinedInput-input': {
    color: '#fff',
  },
}));

const InputMethodsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
}));

const InputMethodChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0, 0.5),
  backgroundColor: 'rgba(138, 43, 226, 0.2)',
  color: '#fff',
  border: '1px solid rgba(138, 43, 226, 0.3)',
  transition: 'all 0.3s ease',
  '&.active': {
    backgroundColor: 'rgba(138, 43, 226, 0.5)',
    boxShadow: '0 0 10px rgba(138, 43, 226, 0.5)',
  },
  '&:hover': {
    backgroundColor: 'rgba(138, 43, 226, 0.3)',
    boxShadow: '0 0 10px rgba(138, 43, 226, 0.3)',
  }
}));

const VoiceRecordingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  backgroundColor: 'rgba(0, 204, 153, 0.1)',
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
  border: '1px solid rgba(0, 204, 153, 0.3)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(0, 204, 153, 0.2))',
    transform: 'rotate(-45deg)',
    transition: 'all 0.5s ease',
    opacity: 0,
  },
  '&:hover': {
    boxShadow: '0 0 15px rgba(0, 204, 153, 0.2)',
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  },
}));

const RecordButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: '#FF6F00',
  padding: theme.spacing(1.5),
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  '&.recording': {
    backgroundColor: 'rgba(255, 111, 0, 0.2)',
    color: '#FF6F00',
    animation: '$pulse 1.5s infinite',
  },
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(255, 111, 0, 0.4)',
    },
    '70%': {
      boxShadow: '0 0 0 10px rgba(255, 111, 0, 0)',
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(255, 111, 0, 0)',
    },
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(0, 0.5),
  textTransform: 'none',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
    transition: 'all 0.5s ease',
  },
  '&:hover::before': {
    left: '100%',
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(0, 0.5),
  textTransform: 'none',
  background: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)',
  color: '#fff',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
    transition: 'all 0.5s ease',
  },
  '&:hover': {
    background: 'linear-gradient(45deg, #9370DB 30%, #8A2BE2 90%)',
    boxShadow: '0 0 15px rgba(138, 43, 226, 0.5)',
    '&::before': {
      left: '100%',
    },
  },
  '&.Mui-disabled': {
    background: 'rgba(138, 43, 226, 0.3)',
  }
}));

const FileUploadInput = styled('input')({
  display: 'none',
});

const AIPromptContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: 'rgba(138, 43, 226, 0.1)',
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.3)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(138, 43, 226, 0.2))',
    transform: 'rotate(-45deg)',
    transition: 'all 0.5s ease',
    opacity: 0,
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 5px 15px rgba(138, 43, 226, 0.2)',
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  },
}));

const AIPromptText = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.9)',
  fontStyle: 'italic',
}));

const PreviewContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: 'rgba(0, 51, 102, 0.3)',
  borderRadius: theme.spacing(1),
  marginTop: theme.spacing(3),
  border: '1px solid rgba(0, 204, 153, 0.3)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(0, 204, 153, 0.2))',
    transform: 'rotate(-45deg)',
    transition: 'all 0.5s ease',
    opacity: 0,
  },
  '&:hover': {
    boxShadow: '0 5px 15px rgba(0, 204, 153, 0.2)',
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  },
}));

const ProblemSubmissionArea: React.FC<ProblemSubmissionAreaProps> = ({ onSubmit, onInputMethodSelected }) => {
  const [inputMethod, setInputMethod] = useState<'text' | 'voice' | 'upload' | 'link'>('text');
  const [problemText, setProblemText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  // const [aiPrompts, setAiPrompts] = useState<string[]>([
  // Using a constant instead of state since we're not changing it
  const aiPrompts = [
    'Consider including specific metrics or KPIs that would define success',
    'What constraints or limitations should be considered?',
    'Are there any existing solutions that partially address this problem?'
  ];
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  
  const handleInputMethodChange = (method: 'text' | 'voice' | 'upload' | 'link') => {
    setInputMethod(method);
    // Notify parent component that an input method was selected
    onInputMethodSelected?.();
  };
  
  const toggleRecording = () => {
    setIsRecording(!isRecording);
    
    if (!isRecording) {
      // Start recording logic would go here
      // For demo, we'll just simulate a recording after 3 seconds
      setTimeout(() => {
        setProblemText('Our customer service response times have increased by 45% over the last quarter, leading to decreased satisfaction scores. We need an AI-powered solution that can handle routine inquiries while maintaining a personal touch.');
        setIsRecording(false);
      }, 3000);
    } else {
      // Stop recording logic would go here
    }
  };
  
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setUploadedFile(files[0]);
      // In a real app, we would parse the file content
      setProblemText(`Uploaded file: ${files[0].name} - Content would be extracted and analyzed here.`);
    }
  };
  
  const handleLinkSubmit = () => {
    if (linkUrl) {
      // In a real app, we would fetch and parse the content from the URL
      setProblemText(`Content from link: ${linkUrl} - Content would be extracted and analyzed here.`);
    }
  };
  
  const handleSubmit = () => {
    if (problemText.trim()) {
      onSubmit(problemText);
      // Navigate to the new workspace page
      navigate('/problem-workspace', { state: { problemStatement: problemText } });
    }
  };
  
  const handleSaveDraft = () => {
    // Logic to save draft would go here
    alert('Problem draft saved!');
  };
  
  return (
    <SubmissionContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <SectionTitle variant="h5">
          Submit Your Problem
        </SectionTitle>
        <Tooltip title="Describe your problem in detail to get the best solutions from our executive surrogates">
          <IconButton size="small" sx={{ ml: 1, color: 'rgba(138, 43, 226, 0.7)' }}>
            <HelpIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      
      <InputContainer>
        <InputMethodsContainer>
          <Box>
            <InputMethodChip 
              label="Text" 
              icon={<TextFieldsIcon />}
              onClick={() => handleInputMethodChange('text')}
              className={inputMethod === 'text' ? 'active' : ''}
            />
            <InputMethodChip 
              label="Voice" 
              icon={<MicIcon />}
              onClick={() => handleInputMethodChange('voice')}
              className={inputMethod === 'voice' ? 'active' : ''}
            />
            <InputMethodChip 
              label="Upload" 
              icon={<UploadIcon />}
              onClick={() => handleInputMethodChange('upload')}
              className={inputMethod === 'upload' ? 'active' : ''}
            />
            <InputMethodChip 
              label="Link" 
              icon={<LinkIcon />}
              onClick={() => handleInputMethodChange('link')}
              className={inputMethod === 'link' ? 'active' : ''}
            />
          </Box>
          
          <Tooltip title="Get help with problem formulation">
            <IconButton size="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </InputMethodsContainer>
        
        <Divider sx={{ mb: 2, backgroundColor: 'rgba(138, 43, 226, 0.3)' }} />
        
        {inputMethod === 'text' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ mb: 3 }}>
              <GradientText gradient="primary" variant="h6">
                Describe Your Problem
              </GradientText>
              <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ mt: 1 }}>
                Be specific and include relevant details to get the best solutions
              </Typography>
            </Box>
            
            <StyledTextField
              fullWidth
              multiline
              rows={6}
              label="Describe your problem in detail"
              variant="outlined"
              value={problemText}
              onChange={(e) => setProblemText(e.target.value)}
              placeholder="What challenge are you trying to solve? Be as specific as possible..."
            />
          </motion.div>
        )}
        
        {inputMethod === 'voice' && (
          <VoiceRecordingContainer>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              {isRecording ? 'Recording... Speak clearly' : 'Click the microphone to start recording'}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isRecording && <VoiceWaveform />}
              
              <RecordButton 
                className={isRecording ? 'recording' : ''}
                onClick={toggleRecording}
              >
                {isRecording ? <MicOffIcon /> : <MicIcon />}
              </RecordButton>
            </Box>
          </VoiceRecordingContainer>
        )}
        
        {inputMethod === 'upload' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <FileUploadInput
                accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                id="file-upload"
                type="file"
                onChange={handleFileUpload}
                ref={fileInputRef}
              />
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ 
                    borderColor: 'rgba(138, 43, 226, 0.5)',
                    color: '#fff',
                    '&:hover': {
                      borderColor: '#8A2BE2',
                      background: 'rgba(138, 43, 226, 0.1)',
                    },
                    px: 3,
                    py: 1.5,
                    borderRadius: '20px',
                  }}
                >
                  Upload Document
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AttachFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ 
                    borderColor: 'rgba(0, 204, 153, 0.5)',
                    color: '#fff',
                    '&:hover': {
                      borderColor: '#00CC99',
                      background: 'rgba(0, 204, 153, 0.1)',
                    },
                    px: 3,
                    py: 1.5,
                    borderRadius: '20px',
                  }}
                >
                  Browse Files
                </Button>
              </Box>
              
              {uploadedFile && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(138, 43, 226, 0.1)', borderRadius: 1 }}>
                  <Typography variant="body2" color="#fff">
                    File: {uploadedFile.name} ({Math.round(uploadedFile.size / 1024)} KB)
                  </Typography>
                </Box>
              )}
              
              <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ mt: 2 }}>
                Supported formats: PDF, DOC, DOCX, TXT
              </Typography>
            </Box>
          </motion.div>
        )}
        
        {inputMethod === 'link' && (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={9}>
                <StyledTextField
                  fullWidth
                  label="Paste URL"
                  variant="outlined"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://..."
                />
              </Grid>
              <Grid item xs={3}>
                <GradientButton
                  fullWidth
                  variant="contained"
                  startIcon={<LinkIcon />}
                  onClick={handleLinkSubmit}
                  sx={{ 
                    height: '56px',
                  }}
                >
                  Extract
                </GradientButton>
              </Grid>
            </Grid>
          </Box>
        )}
        
        {/* AI Prompts */}
        <Box sx={{ mt: 3 }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              color: '#00CCFF',
              textShadow: '0 0 10px rgba(0, 204, 255, 0.5)',
              mb: 1,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            AI Suggestions to Improve Your Problem Statement:
          </Typography>
          
          {aiPrompts.map((prompt, index) => (
            <AIPromptContainer key={index}>
              <AIPromptText variant="body2">
                {prompt}
              </AIPromptText>
            </AIPromptContainer>
          ))}
        </Box>
        
        {/* Preview if there's text */}
        {problemText && (
          <PreviewContainer>
            <Typography variant="subtitle2" sx={{ color: 'rgba(0, 204, 153, 0.9)', mb: 1 }}>
              Problem Summary:
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              {problemText}
            </Typography>
          </PreviewContainer>
        )}
        
        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <ActionButton
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSaveDraft}
            sx={{ 
              borderColor: 'rgba(0, 204, 153, 0.5)',
              color: '#00CC99',
              '&:hover': {
                borderColor: '#00CC99',
                backgroundColor: 'rgba(0, 204, 153, 0.1)',
              }
            }}
          >
            Save Draft
          </ActionButton>
          
          <GradientButton
            variant="contained"
            endIcon={<SendIcon />}
            onClick={handleSubmit}
            disabled={!problemText.trim()}
          >
            Submit Problem
          </GradientButton>
        </Box>
      </InputContainer>
    </SubmissionContainer>
  );
};

export default ProblemSubmissionArea; 